"use client";

import * as React from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { Sidebar } from "@/components/layout/sidebar";
import { Navbar } from "@/components/layout/navbar";
import { DemoBanner } from "@/components/layout/DemoBanner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { useAuthStore } from "@/stores/auth-store";
import { useUIStore } from "@/stores/ui-store";
import { cn } from "@/lib/utils/cn";
import { authApi } from "@/lib/api/auth";

export default function DashboardLayout({
  children,
}: {
  children: React.ReactNode;
}) {
  const router = useRouter();
  const { accessToken, user, setUser, logout } = useAuthStore();
  const { sidebarCollapsed } = useUIStore();
  const [isLoading, setIsLoading] = React.useState(true);
  const [isHydrated, setIsHydrated] = React.useState(false);

  React.useEffect(() => {
    setIsHydrated(true);
  }, []);

  // Guardamos si YA validamos el token contra el backend en esta pestaña.
  // Cache de `user` en localStorage NO es prueba de token vivo: al reabrir
  // el navegador tras días, hay user+token viejos, y sin ping al backend
  // el layout renderiza el dashboard con un token muerto que después revienta
  // en cada query como spinner infinito.
  const [tokenVerified, setTokenVerified] = React.useState(false);

  React.useEffect(() => {
    const checkAuth = async () => {
      if (!isHydrated) return;

      if (!accessToken) {
        logout();
        router.replace("/login");
        return;
      }

      if (tokenVerified) {
        setIsLoading(false);
        return;
      }

      try {
        // Un único ping al backend por carga de la pestaña. Refresca
        // también el user cacheado por si cambió algún campo (nombre,
        // roles) desde otra sesión.
        const userData = await authApi.getCurrentUser();
        setUser(userData);
        setTokenVerified(true);
        setIsLoading(false);
      } catch {
        // Fallo del ping = token muerto. Hard nav a /login para asegurar
        // que ningún query en vuelo se queda colgado.
        logout();
        if (typeof window !== "undefined") {
          window.location.href = "/login";
        }
      }
    };

    checkAuth();
  }, [isHydrated, accessToken, tokenVerified, router, setUser, logout]);

  React.useEffect(() => {
    if (!isLoading && !accessToken) {
      router.replace("/login");
    }
  }, [isLoading, accessToken, router]);

  if (isLoading || !accessToken) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  return (
    <TooltipProvider delayDuration={150} skipDelayDuration={50}>
      <div className="layout">
        <Sidebar />
        <div
          className={cn(
            "layout__wrap flex flex-col",
            sidebarCollapsed && "layout__wrap--slim"
          )}
        >
          <div className="layout__navbar">
            <Navbar />
          </div>
          <DemoBanner />
          <main className="layout__content flex-1">{children}</main>
          <footer className="border-t border-[var(--gray-200)] px-6 py-3 text-xs text-muted-foreground flex flex-wrap items-center justify-between gap-2">
            <span>© {new Date().getFullYear()} Feterand, S.L.</span>
            <div className="flex items-center gap-4">
              <Link href="/aviso-legal" className="hover:underline">
                Aviso legal
              </Link>
              <Link href="/politica-privacidad" className="hover:underline">
                Política de privacidad
              </Link>
              <Link href="/politica-cookies" className="hover:underline">
                Política de cookies
              </Link>
            </div>
          </footer>
        </div>
      </div>
    </TooltipProvider>
  );
}
